import { useState, useEffect, useRef } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { X, Mail, MessageSquare, Phone, FileText, Send, Sparkles, Loader2, Paperclip, Bold, Italic, List } from "lucide-react";
import AttachmentChipsEditor from "@/components/AttachmentChipsEditor";
import ResourcePickerPanel, { AttachFromResourcesLink, AttachedResourcesRow } from "@/components/ResourcePickerPanel";
import RecipientAutocomplete from "@/components/RecipientAutocomplete";
import { buildBrandedEmailHtml } from "@/lib/brandedEmailTemplate";
import { QuoteAttachmentPreview, buildQuotePdf, quotePdfFileName } from "@/components/quotes/QuoteAttachmentPreview";

const CALL_OUTCOMES = ["Connected", "No answer", "Left voicemail", "Callback requested"];
const NOTE_TYPES = ["Meeting", "Email", "Call", "Demo", "Internal note", "Other"];

export default function ComposeModal({ open, onClose, lead, mode: initMode, onSend, quoteContext }) {
  const { token, user } = useAuth();
  const [mode, setMode] = useState(initMode || "email");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [callOutcome, setCallOutcome] = useState("");
  const [callDuration, setCallDuration] = useState("");
  const [callNotes, setCallNotes] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [noteType, setNoteType] = useState("Meeting");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [preparingPdf, setPreparingPdf] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [attachments, setAttachments] = useState([]);    // [{id,name,size,content_type,data_b64}]
  const [youtubeLinks, setYoutubeLinks] = useState([]);  // canonical URLs
  // §5 — resources picked from /api/assets (separate from upload-attachments).
  const [resourceFiles, setResourceFiles] = useState([]); // [{id,name,size,content_type,url,kind}]
  const [resourceVideos, setResourceVideos] = useState([]); // [{id,title,url,provider}]
  const [resourcePickerOpen, setResourcePickerOpen] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (lead) {
      setTo(mode === "sms" ? (lead.phone || "") : (lead.email || ""));
    }
  }, [lead, mode]);

  useEffect(() => { setMode(initMode || "email"); }, [initMode]);

  // Auto-draft quote email when quoteContext is provided
  useEffect(() => {
    if (quoteContext?.quote && open) {
      const q = quoteContext.quote;
      const autoDraft = async () => {
        setDrafting(true);
        try {
          const res = await axios.post(`${API}/ai/quote-email-draft`, {
            quote_title: q.title, quote_number: q.quote_number,
            products: (q.line_items || []).map(i => i.product).join(", "),
            total: q.total || 0, valid_until: q.valid_until || "N/A",
            contact_name: q.contact_name || lead?.contact_name || "",
            company: q.contact_company || lead?.company || "",
            specialty: quoteContext.lead?.specialty || "",
            pain_points: quoteContext.lead?.pain_points?.join(", ") || "",
          }, { headers: { Authorization: `Bearer ${token}` } });
          if (res.data.subject) setSubject(res.data.subject);
          if (res.data.body) { setBody(""); typeWriter(res.data.body, setBody); }
        } catch {
          setSubject(`Your Enable Healthcare Quote — ${q.title || q.quote_number}`);
          setBody(`Hi ${q.contact_name || "there"},\n\nPlease find your personalized quote (${q.quote_number}) for ${q.title}.\n\nEstimated monthly value: $${(q.total || 0).toLocaleString()}/mo\n\nPlease review and let us know if you have any questions.`);
        }
        setDrafting(false);
      };
      autoDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when quote context or open flag changes; lead/token intentionally read fresh
  }, [quoteContext, open]);

  if (!open) return null;

  const draftWithClaude = async () => {
    setDrafting(true);
    try {
      const payload = mode === "sms" ? {
        type: "sms", lead_name: lead?.contact_name, company: lead?.company,
        stage: lead?.stage, sub_status: lead?.sub_status, next_action: lead?.next_action,
        last_contact: lead?.last_contact_summary,
      } : mode === "email" ? {
        type: "email", lead_name: lead?.contact_name, company: lead?.company,
        role: lead?.role, specialty: lead?.specialty, stage: lead?.stage,
        sub_status: lead?.sub_status, pain_points: lead?.pain_points,
        products: lead?.product_interest, next_action: lead?.next_action,
        subject_hint: subject, last_contact: lead?.last_contact_summary,
      } : {
        type: "call_analysis", notes: callNotes, lead_name: lead?.contact_name,
        company: lead?.company, stage: lead?.stage,
      };

      const res = await axios.post(`${API}/ai/draft-communication`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      if (mode === "email") {
        if (data.subject && !subject) setSubject(data.subject);
        if (data.body) { setBody(""); typeWriter(data.body, setBody); }
      } else if (mode === "sms") {
        if (data.body) { setBody(""); typeWriter(data.body, setBody); }
      } else if (mode === "call") {
        if (data.suggestion) setAiSuggestion(data.suggestion);
        if (data.next_action) setNextStep(data.next_action);
      }
    } catch {
      toast.error("Claude unavailable — try again");
    }
    setDrafting(false);
  };

  const typeWriter = (text, setter) => {
    let i = 0;
    const interval = setInterval(() => {
      setter(text.slice(0, i + 1));
      i++;
      if (i >= text.length) clearInterval(interval);
    }, 15);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      if (mode === "sms") {
        const { sendRCSms } = await import("@/lib/rcIntegrationApi");
        await sendRCSms({ to: to || lead?.phone, text: body });
      } else if (mode === "call") {
        // Call logging stays on /communications/send (it's a log, not an RC action)
        await axios.post(`${API}/communications/send`, {
          lead_id: lead?.id, type: "call", direction: "outbound", channel: "call",
          content: callNotes, call_outcome: callOutcome, call_duration: callDuration,
          next_step: nextStep || undefined, to,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Call logged");
      } else if (mode === "email") {
        // Iter 181 (Feb 2026 — Iratzi `01310903`): inline-attach resource
        // files the rep picked from /api/assets, and synthesise a
        // quote-summary attachment when a quoteContext is in play. The
        // previous code rendered both as visual chips on the modal but
        // never actually shipped the bytes — so when iratzi sent a quote
        // email the recipient got the AI-drafted body with neither the
        // quote nor any picked resources attached.
        const expandedAttachments = [...attachments];
        // Download each resource asset, base64-encode, and push into the
        // M365 payload. Skip silently on a single failure so the rest of
        // the email still goes out (we surface a toast at the end).
        const resourceFailures = [];
        for (const rf of resourceFiles) {
          if (!rf?.url) continue;
          // Skip duplicates if the rep also uploaded the same file.
          if (expandedAttachments.some(a => a.name === rf.name && a.size === rf.size)) continue;
          try {
            const r = await axios.get(rf.url, { responseType: "arraybuffer", timeout: 30000 });
            const bytes = new Uint8Array(r.data);
            let bin = "";
            const CHUNK = 32 * 1024;
            for (let i = 0; i < bytes.length; i += CHUNK) {
              bin += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + CHUNK, bytes.length)));
            }
            expandedAttachments.push({
              id: rf.id,
              name: rf.name || "resource",
              size: bytes.length,
              content_type: rf.content_type || "application/octet-stream",
              data_b64: btoa(bin),
            });
          } catch (err) {
            resourceFailures.push(rf.name || rf.id);
          }
        }
        // Attach the EXACT Preview/Download quote PDF (same client renderer).
        // If it can't be produced, stop — never send a different-looking file.
        if (quoteContext?.quote?.id) {
          const q = quoteContext.quote;
          const fname = quotePdfFileName(q);
          if (!expandedAttachments.some(a => a.name === fname)) {
            setPreparingPdf(true);
            try {
              const bytes = await buildQuotePdf(q.id, token, user);
              let bin = "";
              const CHUNK = 32 * 1024;
              for (let i = 0; i < bytes.length; i += CHUNK) {
                bin += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + CHUNK, bytes.length)));
              }
              expandedAttachments.push({
                id: `quote-${q.id}`,
                name: fname,
                size: bytes.length,
                content_type: "application/pdf",
                data_b64: btoa(bin),
              });
            } catch (err) {
              console.error("[compose] quote PDF preparation failed", err);
              toast.error("Couldn't prepare the quote PDF — try again");
              setPreparingPdf(false);
              setSending(false);
              return;
            }
            setPreparingPdf(false);
          }
        }
        // Smart transport: if the caller attached files or YouTube links, try
        // M365 first so the actual payload is delivered. Fall back to
        // /communications/send when M365 isn't connected (metadata logged for
        // audit; rep still gets a clear nudge to connect M365).
        const hasPayload = (expandedAttachments.length > 0) || (youtubeLinks.length > 0);
        let sentViaM365 = false;
        if (hasPayload) {
          try {
            // Iter 186.2 (Feb 2026) — when a quoteContext is attached,
            // wrap the plain body in the Enable Healthcare-branded
            // email envelope so client-facing correspondence matches
            // the PDF brand. Falls back to the raw <br>-converted body
            // for non-quote emails.
            const plainHtml = (body || "").replace(/\n/g, "<br>");
            const bodyHtmlToSend = quoteContext?.quote
              ? buildBrandedEmailHtml(plainHtml, {
                  quoteNumber: quoteContext.quote.number || quoteContext.quote.id || undefined,
                  preparedForOrg: quoteContext.quote.customer_name
                    || quoteContext.quote.client_name
                    || lead?.company
                    || undefined,
                  issuedAt: quoteContext.quote.issued_at
                    || quoteContext.quote.created_at
                    || new Date(),
                })
              : plainHtml;
            await axios.post(`${API}/m365/email/send`, {
              to: [to].filter(Boolean),
              subject: subject || "(no subject)",
              body_html: bodyHtmlToSend,
              lead_id: lead?.id || undefined,
              attachments: expandedAttachments.map(a => ({ name: a.name, content_type: a.content_type, data_b64: a.data_b64 })),
              youtube_links: youtubeLinks,
            }, { headers: { Authorization: `Bearer ${token}` } });
            sentViaM365 = true;
            toast.success(`Email sent via Microsoft 365 (${expandedAttachments.length} file${expandedAttachments.length === 1 ? "" : "s"}${youtubeLinks.length ? `, ${youtubeLinks.length} YouTube` : ""})`);
          } catch (err) {
            const detail = err?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.code : null;
            if (err?.response?.status === 422) {
              toast.error(typeof detail === "object" ? detail.message : "Attachment or YouTube link rejected");
              setSending(false);
              return;
            }
            if (err?.response?.status === 400 && code === "m365_not_connected") {
              toast.error("Connect Microsoft 365 in Settings to send attachments");
              setSending(false);
              return;
            }
            // Other failures: fall through to communications/send so the
            // message is still logged; surface a warning.
            toast.warning("M365 send failed — logging message without delivery");
          }
        }
        if (resourceFailures.length) {
          toast.warning(`Couldn't attach ${resourceFailures.length} resource file(s): ${resourceFailures.slice(0,2).join(", ")}${resourceFailures.length > 2 ? "…" : ""}`);
        }
        // Always log to /communications/send too — keeps a single source of
        // truth for the CRM activity feed. Send metadata only (no bytes).
        await axios.post(`${API}/communications/send`, {
          lead_id: lead?.id, type: "email", direction: "outbound", channel: "email",
          subject, content: body, next_step: nextStep || undefined, to,
          attachments_meta: expandedAttachments.map(a => ({ name: a.name, size: a.size, content_type: a.content_type })),
          youtube_links: youtubeLinks,
          quote_id: quoteContext?.quote?.id || undefined,
        }, { headers: { Authorization: `Bearer ${token}` } });
        // Iter 183 (Iratzi #305838b7 Feb 2026) — after a successful
        // email send, flip the quote into the Sent section and append
        // to its delivery log. Best-effort: if M365 didn't deliver we
        // still mark it so the rep doesn't have to manually push the
        // status forward in the UI. Errors are non-fatal (the email
        // already went out — at worst the Sent tab is a beat behind).
        if (quoteContext?.quote?.id && sentViaM365) {
          try {
            await axios.post(`${API}/quotes/${quoteContext.quote.id}/mark-sent`, {
              recipient_email: to || quoteContext.quote.contact_email || "",
              channel: "email",
              subject: subject || "",
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch (err) {
            // Surface a soft warning so the rep can manually nudge the
            // status from the Quotes page if they care.
            toast.warning("Email delivered, but couldn't update the quote's Sent log automatically.");
          }
        }
        if (!sentViaM365) toast.success("Email sent");
      } else {
        // Activity Note — existing flow unchanged.
        await axios.post(`${API}/communications/send`, {
          lead_id: lead?.id, type: mode, direction: "outbound",
          channel: mode === "email" ? "email" : "note",
          subject: mode === "email" ? subject : undefined,
          content: body, note_type: mode === "note" ? noteType : undefined,
          next_step: nextStep || undefined, to,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Note saved");
      }
      if (onSend) onSend({ type: mode });
      onClose();
      setSubject(""); setBody(""); setCallNotes(""); setCallOutcome(""); setNextStep(""); setAiSuggestion("");
      setAttachments([]); setYoutubeLinks([]);
      setResourceFiles([]); setResourceVideos([]);
    } catch {
      toast.error("Failed to send — try again");
    }
    setSending(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.38)" }} />
      <div onClick={e => e.stopPropagation()} data-testid="compose-modal"
        style={{ position: "relative", width: 620, background: "var(--surface)", borderRadius: 13, boxShadow: "0 24px 64px rgba(0,0,0,0.16)", display: "flex", flexDirection: "column", maxHeight: "85vh" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "0.5px solid var(--border)" }}>
          <div style={{ fontSize: 14, fontWeight: 650 }}>
            {mode === "call" ? "Log call" : mode === "note" ? "Activity note" : "New message"} — {lead?.contact_name || lead?.title || "Select lead"}
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: "50%", border: "0.5px solid var(--border)", background: "var(--surface)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X style={{ width: 14, height: 14, color: "var(--text-tertiary)" }} />
          </button>
        </div>

        {/* Mode tabs */}
        <div style={{ display: "flex", borderBottom: "0.5px solid var(--border)" }}>
          {[
            { id: "email", label: "Email", icon: Mail },
            { id: "sms", label: "SMS", icon: MessageSquare },
            { id: "call", label: "Log Call", icon: Phone },
            { id: "note", label: "Activity Note", icon: FileText },
          ].map(t => (
            <button key={t.id} onClick={() => setMode(t.id)} data-testid={`compose-tab-${t.id}`}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "9px 0", fontSize: 12, fontWeight: mode === t.id ? 600 : 400, color: mode === t.id ? "var(--blue)" : "var(--text-tertiary)", background: "none", border: "none", borderBottom: mode === t.id ? "2px solid var(--blue)" : "2px solid transparent", cursor: "pointer" }}>
              <t.icon style={{ width: 13, height: 13 }} /> {t.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "14px 20px" }}>
          {/* Email mode */}
          {mode === "email" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>To</label>
                <RecipientAutocomplete value={to} onChange={setTo} testid="compose-to" />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Subject</label>
                <input className="input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject line..." style={{ marginTop: 3 }} data-testid="compose-subject" />
              </div>
              {/* Quote attachment indicator */}
              {quoteContext?.quote && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "var(--blue-light)", borderRadius: 6, marginBottom: 10, fontSize: 11 }} data-testid="quote-attachment-indicator">
                  <FileText style={{ width: 12, height: 12, color: "var(--blue)" }} />
                  <span style={{ fontWeight: 600, color: "var(--blue)" }}>{quoteContext.quote.title} — {quoteContext.quote.quote_number}</span>
                  <span style={{ color: "var(--text-tertiary)" }}>&middot;</span>
                  <span style={{ color: "var(--blue)", fontWeight: 600 }}>${(quoteContext.quote.total || 0).toLocaleString()}/mo</span>
                  {quoteContext.quote.id && <QuoteAttachmentPreview quote={quoteContext.quote} token={token} user={user} />}
                </div>
              )}
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                  <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Body</label>
                  <div style={{ display: "flex", gap: 2 }}>
                    <button style={{ padding: "2px 4px", background: "none", border: "0.5px solid var(--border)", borderRadius: 3, cursor: "pointer" }}><Bold style={{ width: 11, height: 11, color: "var(--text-tertiary)" }} /></button>
                    <button style={{ padding: "2px 4px", background: "none", border: "0.5px solid var(--border)", borderRadius: 3, cursor: "pointer" }}><Italic style={{ width: 11, height: 11, color: "var(--text-tertiary)" }} /></button>
                    <button style={{ padding: "2px 4px", background: "none", border: "0.5px solid var(--border)", borderRadius: 3, cursor: "pointer" }}><List style={{ width: 11, height: 11, color: "var(--text-tertiary)" }} /></button>
                  </div>
                </div>
                <textarea ref={bodyRef} className="input" rows={8} value={body} onChange={e => setBody(e.target.value)} placeholder="Write your message..." data-testid="compose-body" style={{ minHeight: 180 }} />
              </div>
              <AttachmentChipsEditor
                attachments={attachments}
                youtube={youtubeLinks}
                onAttachmentsChange={setAttachments}
                onYouTubeChange={setYoutubeLinks}
              />
              <AttachedResourcesRow
                files={resourceFiles}
                videos={resourceVideos}
                onRemoveFile={(i) => setResourceFiles((f) => f.filter((_, idx) => idx !== i))}
                onRemoveVideo={(i) => setResourceVideos((v) => v.filter((_, idx) => idx !== i))}
              />
            </>
          )}

          {/* SMS mode */}
          {mode === "sms" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>To</label>
                <input className="input" value={to} onChange={e => setTo(e.target.value)} style={{ marginTop: 3 }} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Message</label>
                <textarea className="input" rows={4} value={body} onChange={e => { setBody(e.target.value); setCharCount(e.target.value.length); }} placeholder="Type SMS..." style={{ marginTop: 3, minHeight: 80 }} data-testid="compose-sms-body" />
                <div style={{ textAlign: "right", fontSize: 10, color: charCount > 140 ? "var(--red)" : "var(--text-tertiary)", marginTop: 2 }}>{charCount}/160</div>
              </div>
            </>
          )}

          {/* Call Log mode */}
          {mode === "call" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Contact</label>
                <div style={{ padding: "8px 12px", background: "var(--gray-light)", borderRadius: 6, fontSize: 12, color: "var(--text-secondary)", marginTop: 3 }}>{lead?.contact_name || "—"} · {lead?.company || ""}</div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Duration (minutes)</label>
                <input className="input" type="number" value={callDuration} onChange={e => setCallDuration(e.target.value)} placeholder="0" style={{ marginTop: 3, width: 100 }} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase", display: "block", marginBottom: 4 }}>Outcome</label>
                <div style={{ display: "flex", gap: 4 }}>
                  {CALL_OUTCOMES.map(o => (
                    <button key={o} onClick={() => setCallOutcome(o)} data-testid={`outcome-${o.toLowerCase().replace(/\s/g,"-")}`}
                      style={{ padding: "6px 12px", fontSize: 11, borderRadius: 6, border: "0.5px solid var(--border)", cursor: "pointer", background: callOutcome === o ? "var(--blue)" : "var(--surface)", color: callOutcome === o ? "#fff" : "var(--text-secondary)", fontWeight: callOutcome === o ? 600 : 400 }}>
                      {o}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Notes</label>
                <textarea className="input" rows={5} value={callNotes} onChange={e => setCallNotes(e.target.value)} placeholder="What was discussed? Any next steps mentioned? Key takeaways..." style={{ marginTop: 3, minHeight: 120 }} data-testid="compose-call-notes" />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Next step I committed to</label>
                <input className="input" value={nextStep} onChange={e => setNextStep(e.target.value)} placeholder="e.g. Send proposal by Thursday" style={{ marginTop: 3 }} />
              </div>
            </>
          )}

          {/* Activity Note mode */}
          {mode === "note" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase", display: "block", marginBottom: 4 }}>Type</label>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {NOTE_TYPES.map(t => (
                    <button key={t} onClick={() => setNoteType(t)}
                      style={{ padding: "4px 10px", fontSize: 11, borderRadius: 20, border: "0.5px solid var(--border)", cursor: "pointer", background: noteType === t ? "var(--blue-light)" : "var(--surface)", color: noteType === t ? "var(--blue)" : "var(--text-secondary)", fontWeight: noteType === t ? 600 : 400 }}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Note</label>
                <textarea className="input" rows={5} value={body} onChange={e => setBody(e.target.value)} placeholder="Enter details..." style={{ marginTop: 3, minHeight: 100 }} data-testid="compose-note-body" />
              </div>
            </>
          )}

          {/* AI Suggestion */}
          {aiSuggestion && (
            <div style={{ marginTop: 10, padding: "8px 12px", background: "var(--blue-light)", borderRadius: 8, fontSize: 11, color: "var(--blue)" }}>
              <Sparkles style={{ width: 10, height: 10, marginRight: 4, display: "inline" }} />
              Claude: {aiSuggestion}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: "0.5px solid var(--border)", padding: "10px 20px", display: "flex", alignItems: "center", gap: 6 }}>
          {(mode === "email" || mode === "sms" || mode === "call") && (
            <button className="btn" onClick={draftWithClaude} disabled={drafting} data-testid="draft-claude-btn"
              style={{ color: "var(--blue)", borderColor: "var(--blue)" }}>
              {drafting ? <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> : <Sparkles style={{ width: 12, height: 12 }} />}
              {drafting ? "Claude is writing..." : mode === "call" ? "Analyze with Claude" : "Draft with Claude"}
            </button>
          )}
          {mode === "email" && (
            <AttachFromResourcesLink onClick={() => setResourcePickerOpen(true)} />
          )}
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending} data-testid="compose-send-btn">
            {sending ? <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> : <Send style={{ width: 12, height: 12 }} />}
            {preparingPdf ? "Preparing quote PDF…" : sending ? "Sending..." : mode === "call" ? "Log call" : mode === "note" ? "Save note" : "Send"}
          </button>
        </div>
        <ResourcePickerPanel
          open={resourcePickerOpen}
          onClose={() => setResourcePickerOpen(false)}
          onAttachFile={(f) => setResourceFiles((prev) => prev.find((x) => x.id === f.id) ? prev : [...prev, f])}
          onInsertVideoLink={(v) => {
            setResourceVideos((prev) => prev.find((x) => x.id === v.id) ? prev : [...prev, v]);
            // Also append a formatted block to the email body so the rep
            // sees it in-line — matches §5B "Watch: …" link block spec.
            const block = `\n\n▶ Watch: ${v.title}\n${v.url}\n`;
            setBody((b) => (b || "") + block);
          }}
        />
      </div>
    </div>
  );
}
